import ThirdPartSoftware from "@/components/third-part-softwares/third-part-softwares";
import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import SettingsFormContainer, {
  SettingsFormHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { LockedOverlay } from "@/components/plan-mini/locked-overlay";
import { PlanUpgradeCTA } from "@/components/plan-mini/plan-upgrade-cta";
import APIKeyCard from "./components/api-key/api-key";
import ExportSettings from "./components/export-settings/export-settings";
import WebhooksList from "./components/webhooks/webhooks-list";
import ImportSettings from "./components/import-settings/import-settings";

export default function IntegrationsPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const isRetractionOnly = useIsRetractionOnly();

  return (
    <SettingsFormContainer>
      <SettingsFormHeader
        title={t("sidebar.integrations")}
        description={
          "Configure how Baback integrates with your other tools and services."
        }
      />

      {isRetractionOnly ? (
        <div className="flex flex-col gap-4">
          {/* API Token */}
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="text-sm font-semibold mb-3">
                {t("settings.integrations.api.title")}
              </div>
              <div className="flex items-center gap-3 rounded-md border bg-background px-4 py-3">
                <div className="h-4 w-4 rounded bg-muted/60" />
                <div className="flex-1 font-mono text-xs text-muted-foreground">
                  bk_live_••••••••••••••••••••••••••••••••
                </div>
                <div className="h-7 w-16 rounded bg-muted/50 border" />
              </div>
            </div>
            <LockedOverlay
              title={t("settings.integrations.api.title")}
              description="Accédez à l'API Baback pour connecter votre back-office ou vos outils internes. Disponible dans le plan Lite."
              withCta
            />
          </div>

          {/* Exports */}
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="space-y-0.5 mb-3">
                <div className="text-sm font-semibold">
                  {t("settings.integrations.exports.title")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("settings.integrations.exports.subtitle")}
                </div>
              </div>
              {["Export FTP quotidien (CSV)", "Export SFTP hebdo (JSON)"].map(
                (ex) => (
                  <div
                    key={ex}
                    className="flex items-center justify-between rounded-md border bg-background px-4 py-2 text-xs"
                  >
                    <span className="font-medium">{ex}</span>
                    <div className="h-4 w-8 rounded-full bg-muted border" />
                  </div>
                ),
              )}
            </div>
            <LockedOverlay
              title={t("settings.integrations.exports.title")}
              description="Programmez des exports automatiques de vos retours vers votre FTP ou SFTP."
            />
          </div>

          {/* Imports */}
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="space-y-0.5 mb-3">
                <div className="text-sm font-semibold">
                  {t("settings.integrations.imports.title")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("settings.integrations.imports.subtitle")}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border bg-background px-4 py-2 text-xs">
                <span className="font-medium">Import SFTP logistique</span>
                <div className="h-4 w-8 rounded-full bg-muted border" />
              </div>
            </div>
            <LockedOverlay
              title={t("settings.integrations.imports.title")}
              description="Importez automatiquement les données logistiques depuis votre système de gestion."
            />
          </div>

          {/* Webhooks */}
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="space-y-0.5 mb-3">
                <div className="text-sm font-semibold">
                  {t("settings.integrations.webhooks.title")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("settings.integrations.webhooks.subtitle")}
                </div>
              </div>
              {[
                "https://hooks.votreboutique.com/retours",
                "https://api.moncrm.io/baback",
              ].map((url) => (
                <div
                  key={url}
                  className="flex items-center justify-between rounded-md border bg-background px-4 py-2 text-xs"
                >
                  <span className="font-mono text-muted-foreground truncate max-w-[280px]">
                    {url}
                  </span>
                  <div className="h-4 w-8 rounded-full bg-muted border" />
                </div>
              ))}
            </div>
            <LockedOverlay
              title={t("settings.integrations.webhooks.title")}
              description="Recevez des notifications en temps réel sur vos endpoints lors d'événements Baback."
            />
          </div>

          {/* Intégrations tierces */}
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="space-y-0.5 mb-3">
                <div className="text-sm font-semibold">
                  {t("settings.integrations.thirdpart.title")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("settings.integrations.thirdpart.subtitle")}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "Gorgias",
                  "Klaviyo",
                  "Zendesk",
                  "Brevo",
                  "Stripe",
                  "ShippingBo",
                ].map((app) => (
                  <div
                    key={app}
                    className="rounded-md border bg-background px-3 py-1.5 text-xs font-medium"
                  >
                    {app}
                  </div>
                ))}
              </div>
            </div>
            <LockedOverlay
              title={t("settings.integrations.thirdpart.title")}
              description="Connectez Baback à votre CRM, helpdesk ou outil marketing. Disponible dans le plan Lite."
              withCta
            />
          </div>
        </div>
      ) : (
        <>
          <APIKeyCard />
          <ExportSettings />
          <ImportSettings />
          <WebhooksList />
          <ThirdPartSoftware />
        </>
      )}
    </SettingsFormContainer>
  );
}
